import {
   getPageFindReason,
   pageFindConfidenceStatus,
   pagesDifferFromAi,
} from "src/modules/ANNModule/ANNServitutProvenance";
import type { ServitutAnalysisFieldKey } from "src/modules/ANNModule/ANNServitutFieldAnnotations";
export type ServitutDocumentExtent =
   | { kind: "missing" }
   | { kind: "entire_akt" };

export type ServitutPageSourceStatus =
   | { kind: "hidden" }
   | { kind: "not_run" }
   | { kind: "ai_none" }
   | { kind: "ai_pages"; count: number; confident: boolean; reason?: string }
   | { kind: "user_pages"; count: number };
import { fieldDisplayText } from "../model/projectAnn";
import { orderedRows, type ServitutReportState } from "../model/report";
import { historicalPaataleName, isResolvableHistoricalPaatale } from "../model/projectAnn";
import {
   hasAcceptedField,
   isDigitalRow,
   isFetched,
   isUserFileDocument,
   type ServitutRow,
} from "../model/row";

export function selectOrderedRows(state: ServitutReportState): ServitutRow[] {
   return orderedRows(state);
}

export function selectRow(state: ServitutReportState, id: string): ServitutRow | undefined {
   return state.rows[id];
}

export function selectIsApproved(row: ServitutRow): boolean {
   return row.review.state === "checked";
}

export function selectIsRejected(row: ServitutRow): boolean {
   return row.review.state === "rejected";
}

export function selectIsControlled(row: ServitutRow): boolean {
   return row.review.state === "checked" || row.review.state === "rejected";
}

export function selectIdentityLabel(row: ServitutRow): string {
   return row.identity.datoloebenr || row.landRegistry.displayAktId || "";
}

export function selectDocumentOrigin(row: ServitutRow): "none" | "tingbog" | "digital" | "upload" | "rigsarkivet" {
   const document = row.document;
   if (document.kind === "none") {
      return "none";
   }
   if (document.kind === "rigsarkivet") {
      return "rigsarkivet";
   }
   if (document.kind === "upload") {
      return "upload";
   }
   if (document.kind === "tingbog_digital") {
      return "digital";
   }
   if (document.kind === "annotation") {
      if (document.origin === "upload") {
         return "upload";
      }
      if (document.origin === "digital") {
         return "digital";
      }
      return "tingbog";
   }
   return "tingbog";
}

export function selectIsFileOrigin(row: ServitutRow): boolean {
   return selectDocumentOrigin(row) === "upload";
}

export function selectWorkingDocumentLabel(row: ServitutRow): string {
   const document = row.document;
   if (document.kind === "upload") {
      return document.filename;
   }
   if (document.kind === "annotation" && document.filename) {
      return document.filename;
   }
   if (document.kind === "tingbog_akt") {
      return document.filename || document.aktId || selectExportFileName(row);
   }
   return selectExportFileName(row);
}

export function selectHeading(row: ServitutRow): string {
   return (
      row.analysis?.item.summary
      || row.landRegistry.summary
      || ""
   ).toString().trim();
}

export function selectPageSource(row: ServitutRow): ServitutPageSourceStatus {
   if (isDigitalRow(row)) {
      return { kind: "hidden" };
   }
   const selected = row.pages.selected;
   const aiPages = row.pages.aiSuggested;
   const hasAi = row.analysis?.item ? Array.isArray(row.analysis.item.pages_kept) : aiPages.length > 0;
   if (selected.length > 0 && (!hasAi || pagesDifferFromAi(selected, aiPages, false))) {
      return { kind: "user_pages", count: selected.length };
   }
   if (!hasAi) {
      return { kind: "not_run" };
   }
   if (!aiPages.length) {
      return { kind: "ai_none" };
   }
   const pagesStatus = pageFindConfidenceStatus(row.analysis?.item, aiPages);
   return {
      kind: "ai_pages",
      count: aiPages.length,
      confident: pagesStatus !== "ai_ikke_glad",
      reason: getPageFindReason(row.analysis?.item),
   };
}

export function selectDocumentExtent(row: ServitutRow): ServitutDocumentExtent {
   if (!isFetched(row)) {
      return { kind: "missing" };
   }
   if (isDigitalRow(row) || selectIsFileOrigin(row)) {
      return { kind: "missing" };
   }
   if (selectPageSource(row).kind === "not_run") {
      return { kind: "entire_akt" };
   }
   return { kind: "missing" };
}

export function selectHasChosenPages(row: ServitutRow): boolean {
   if (isDigitalRow(row) && !isUserFileDocument(row)) {
      return true;
   }
   return row.pages.selected.length > 0;
}

export function selectPageApprovalPlan(row: ServitutRow): {
   pagesToSave: number[];
   electronic: boolean;
   differs: boolean;
   canApprove: boolean;
} {
   const electronic = isDigitalRow(row) && !isUserFileDocument(row);
   const pagesToSave = row.pages.selected;
   return {
      pagesToSave,
      electronic,
      differs: pagesDifferFromAi(pagesToSave, row.pages.aiSuggested, electronic),
      canApprove: isFetched(row) && row.job === "idle" && selectHasChosenPages(row),
   };
}

export type ServitutPageControlTone = "approved" | "rejected" | "good" | "warn" | "bad" | "pending";

export function selectPageControlTone(row: ServitutRow): ServitutPageControlTone {
   if (row.review.state === "checked") {
      return "approved";
   }
   if (row.review.state === "rejected") {
      return "rejected";
   }
   if (isDigitalRow(row) && isFetched(row)) {
      return "good";
   }
   const source = selectPageSource(row);
   if (source.kind === "user_pages") {
      return "pending";
   }
   if (source.kind === "ai_none") {
      return "bad";
   }
   if (source.kind === "ai_pages") {
      return source.confident ? "good" : "warn";
   }
   return "pending";
}

export function selectFieldText(
   row: ServitutRow,
   field: ServitutAnalysisFieldKey,
   caseDescription?: string,
   options?: { preferLiveAi?: boolean },
): string {
   return fieldDisplayText(row, field, caseDescription, options);
}

export function selectFieldUntouchedAccepted(
   row: ServitutRow,
   field: ServitutAnalysisFieldKey,
): boolean {
   return hasAcceptedField(row, field) && !row.fieldOverridePending?.[field];
}

export function selectDisplayedFieldText(
   state: ServitutReportState,
   row: ServitutRow,
   field: ServitutAnalysisFieldKey,
): string {
   return selectFieldText(row, field, state.caseDescription, {
      preferLiveAi: Boolean(row.fieldOverridePending?.[field]),
   });
}

export function selectIncompleteARows(state: ServitutReportState): ServitutRow[] {
   return selectOrderedRows(state).filter((row) => !selectIsControlled(row));
}

export function selectRejectedARows(state: ServitutReportState): ServitutRow[] {
   return selectOrderedRows(state).filter(selectIsRejected);
}

export function selectCanLocate(row: ServitutRow): boolean {
   if (row.document.kind === "rigsarkivet") {
      return false;
   }
   if (isDigitalRow(row) && !isUserFileDocument(row)) {
      return false;
   }
   return isFetched(row) || row.document.kind === "tingbog_akt" || row.document.kind === "upload";
}

export function selectHistoricalPaataleName(row: ServitutRow): string {
   return historicalPaataleName(row);
}

export function selectCanResolvePaatale(row: ServitutRow): boolean {
   return isResolvableHistoricalPaatale(selectHistoricalPaataleName(row));
}

export function selectHasLocatableRows(state: ServitutReportState): boolean {
   return selectOrderedRows(state).some(selectCanLocate);
}

export function selectHasContentAnalyzableRows(state: ServitutReportState): boolean {
   return selectOrderedRows(state).some((row) => selectIsApproved(row) && isFetched(row));
}

export function selectAiJobIds(state: ServitutReportState): Set<string> {
   return new Set(
      selectOrderedRows(state)
         .filter((row) => row.job === "locating" || row.job === "analyzing")
         .map((row) => row.id),
   );
}

export function selectSavingIds(state: ServitutReportState): Set<string> {
   return new Set(
      selectOrderedRows(state).filter((row) => row.job === "saving").map((row) => row.id),
   );
}

export function selectAktFetchProgress(state: ServitutReportState): { completed: number; total: number } | null {
   if (!state.aktFetchIds.length) {
      return null;
   }
   const remaining = state.aktFetchIds.filter((id) => state.rows[id]?.job === "fetching").length;
   if (!remaining) {
      return null;
   }
   return {
      completed: state.aktFetchIds.length - remaining,
      total: state.aktFetchIds.length,
   };
}

export function selectContentAnalysisMode(
   row: ServitutRow,
   metadata?: { id?: string; aktId?: string | null; document?: string | null },
): "xml" | "pdf" | "none" {
   if (isUserFileDocument(row)) {
      return "pdf";
   }
   const hasXml = Boolean(metadata && metadata.aktId == null && String(metadata.document || "").trim());
   if (isDigitalRow(row)) {
      return hasXml ? "xml" : "none";
   }
   if (row.document.kind === "annotation") {
      return "pdf";
   }
   return "pdf";
}

export function selectDocumentSourceKind(
   row: ServitutRow,
): "tingbog" | "annotation" | "customUpload" | "rigsarkivetRequired" {
   if (row.document.kind === "annotation") {
      return "annotation";
   }
   if (row.document.kind === "upload") {
      return "customUpload";
   }
   if (row.document.kind === "rigsarkivet") {
      return "rigsarkivetRequired";
   }
   return "tingbog";
}

export function selectRowAktId(row: ServitutRow): string | undefined {
   return row.document.kind === "tingbog_akt" ? row.document.aktId : undefined;
}

export function selectExportFileName(row: ServitutRow): string {
   if (row.document.kind === "upload" && row.document.filename) {
      return row.document.filename;
   }
   if (row.document.kind === "annotation" && row.document.filename) {
      return row.document.filename;
   }
   return row.landRegistry.exportFileName || `${row.landRegistry.displayAktId || row.id}.pdf`;
}
